import { Box } from "@chakra-ui/react";
import { colors, customEvent, Field, Spacing } from "goi_common";
import { BaseStack } from "goi_common/src/design-system";
import { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";
import { PrevNext } from "../RequestQuestionUniqueStep/RequestQuestionStepByStep";
import {
  CREMA_Q1_OPTIONS,
  CREMA_Q1_TITLE,
  CREMA_Q2_ADVANCE_OPTIONS,
  CREMA_Q2_ADVANCE_TITLE,
  CREMA_Q2_URGENT_OPTIONS,
  CREMA_Q2_URGENT_TITLE,
  CREMA_Q3_OPTIONS,
  CREMA_Q3_TITLE,
  CrematoriumScreen,
} from "./crematoriumConstants";
import { CrematoriumTerminalScreen } from "./CrematoriumTerminalScreen";
import { CrematoriumInfoFunnel } from "./CrematoriumInfoFunnel";
import { CrematoriumGuideScreen } from "./CrematoriumGuideScreen";
import { markCrematoriumEntry, getCrematoriumEntryId } from "@/utils/crematoriumEntryContext";
import { getCrematoriumPhoneByRouteId } from "@/utils/crematoriumPhone";

/**
 * 화장장 견적 퍼널 컨테이너. 라우트 없이 내부 스크린 상태머신으로만 전환.
 *
 * 흐름:
 *   Q1(시점) → Q2(상황, urgent/advance) → 종료화면(임종/개장이장) 또는 Q3(궁금한점)
 *   → 정보 퍼널(4스텝) → 가이드
 *
 * 질문 화면: mubinso와 동일한 Field.ButtonGroups + PrevNext(이전/다음) 패턴
 */
const getCremaCtaSubtitle = (cremaCta: string | string[] | undefined): string | null => {
  if (typeof cremaCta !== "string") return null;
  if (cremaCta === "cost") return "설문 3가지만 답하시면 화장 비용에 대한 자세한 안내를 해드려요";
  if (cremaCta === "booking")
    return "설문 3가지만 답하시면 화장 예약에 대한 자세한 안내를 해드려요";
  return null;
};

export default function CrematoriumFunnelContainer() {
  const router = useRouter();
  const [screen, setScreen] = useState<CrematoriumScreen>("q1");
  const [q1Answer, setQ1Answer] = useState("");
  const [q2Answer, setQ2Answer] = useState("");
  const [q3Answer, setQ3Answer] = useState("");

  const cremaCtaSubtitle = getCremaCtaSubtitle(router.query.crema_cta);

  // 진입 시 화장장 ID를 세션에 저장 (개장이장 화면에서 해당 화장장 번호 표시용)
  useEffect(() => {
    markCrematoriumEntry(router.query.crematorium_id);
  }, [router.query.crematorium_id]);

  // 세션에 저장된 화장장 전화번호 (개장이장 종료 화면용)
  const crematoriumPhone = useMemo(() => {
    const id = getCrematoriumEntryId();
    return getCrematoriumPhoneByRouteId(id ?? undefined);
  }, []);

  // 화면 전환 시 스크롤 최상단
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [screen]);

  // Q1 옵션 label → next screen 매핑
  const q1OptionByLabel = (label: string) => CREMA_Q1_OPTIONS.find((o) => o.label === label);

  // Q1 - 화장장 문의 시점
  if (screen === "q1") {
    return (
      <CrematoriumQuestionScreen
        title={CREMA_Q1_TITLE}
        subtitle={cremaCtaSubtitle}
        options={CREMA_Q1_OPTIONS.map((o) => o.label)}
        value={q1Answer}
        onChange={setQ1Answer}
        onNext={() => {
          const opt = q1OptionByLabel(q1Answer);
          if (opt) {
            customEvent({
              newGtm: { path: "crema_funnel", trigger_and_target: opt.event },
            });
            setScreen(opt.next);
          }
        }}
        onPrev={() => {}}
        prevDisabled
      />
    );
  }

  // Q2 (urgent) - 당장 필요
  if (screen === "q2_urgent") {
    return (
      <CrematoriumQuestionScreen
        title={CREMA_Q2_URGENT_TITLE}
        options={CREMA_Q2_URGENT_OPTIONS.map((o) => o.label)}
        value={q2Answer}
        onChange={setQ2Answer}
        onNext={() => {
          const opt = CREMA_Q2_URGENT_OPTIONS.find((o) => o.label === q2Answer);
          if (opt) {
            customEvent({
              newGtm: { path: "crema_funnel", trigger_and_target: opt.event },
            });
            setScreen(opt.next);
          }
        }}
        onPrev={() => {
          setQ2Answer("");
          setScreen("q1");
        }}
      />
    );
  }

  // Q2 (advance) - 미리 알아보기
  if (screen === "q2_advance") {
    return (
      <CrematoriumQuestionScreen
        title={CREMA_Q2_ADVANCE_TITLE}
        options={CREMA_Q2_ADVANCE_OPTIONS.map((o) => o.label)}
        value={q2Answer}
        onChange={setQ2Answer}
        onNext={() => {
          const opt = CREMA_Q2_ADVANCE_OPTIONS.find((o) => o.label === q2Answer);
          if (opt) {
            customEvent({
              newGtm: { path: "crema_funnel", trigger_and_target: opt.event },
            });
            setScreen(opt.next);
          }
        }}
        onPrev={() => {
          setQ2Answer("");
          setScreen("q1");
        }}
      />
    );
  }

  // 종료 화면 - 임종
  if (screen === "terminal_death") {
    return <CrematoriumTerminalScreen type="death" />;
  }

  // 종료 화면 - 개장이장
  if (screen === "terminal_reloc") {
    return <CrematoriumTerminalScreen type="relocation" crematoriumPhone={crematoriumPhone} />;
  }

  // Q3 - 궁금한 점
  if (screen === "q3") {
    return (
      <CrematoriumQuestionScreen
        title={CREMA_Q3_TITLE}
        options={CREMA_Q3_OPTIONS.map((o) => o.label)}
        value={q3Answer}
        onChange={setQ3Answer}
        onNext={() => {
          const opt = CREMA_Q3_OPTIONS.find((o) => o.label === q3Answer);
          if (opt) {
            customEvent({
              newGtm: { path: "crema_funnel", trigger_and_target: opt.event },
            });
            setScreen(opt.next);
          }
        }}
        onPrev={() => {
          setQ3Answer("");
          setScreen(q1Answer === CREMA_Q1_OPTIONS[0].label ? "q2_urgent" : "q2_advance");
        }}
      />
    );
  }

  // 정보 퍼널 (4스텝)
  if (screen === "funnel") {
    return (
      <CrematoriumInfoFunnel
        q1Answer={q1Answer}
        q2Answer={q2Answer}
        q3Answer={q3Answer}
        onComplete={() => setScreen("guide")}
      />
    );
  }

  // 가이드 화면
  return <CrematoriumGuideScreen />;
}

// ---------------------------------------------------------------------------
// 질문 화면 - 무빈소와 동일한 Field.ButtonGroups + PrevNext 패턴
// ---------------------------------------------------------------------------
function CrematoriumQuestionScreen({
  title,
  subtitle,
  options,
  value,
  onChange,
  onNext,
  onPrev,
  prevDisabled = false,
}: {
  title: string;
  subtitle?: string | null;
  options: string[];
  value: string;
  onChange: (answer: string) => void;
  onNext: () => void;
  onPrev: () => void;
  prevDisabled?: boolean;
}) {
  return (
    <BaseStack width="100%" maxW="480px" m="0 auto" minH="100dvh" px={16}>
      <Spacing gap={80} />
      <Box width="100%" whiteSpace="pre-line" className="head2_b black">
        {title}
      </Box>
      {subtitle && (
        <>
          <Spacing gap={8} />
          <Box width="100%" className="body_2_normal_m" color={colors.gray600}>
            {subtitle}
          </Box>
        </>
      )}

      <Spacing gap={24} />
      <Box pt="24px" width="100%">
        <Field.ButtonGroups
          items={options}
          value={value}
          onChange={onChange}
          buttonWidth="100%"
          borderColor={colors.white}
        />
      </Box>

      <Spacing gap={24} />
      <BaseStack
        direction="horizontal"
        width="100%"
        alignItems="center"
        justifyContent="space-between"
        spacing={24}
        pt={4}
      >
        <Box width="100%">
          <PrevNext disabled={prevDisabled} text="이전" variant="filled" onClick={onPrev} />
        </Box>
        <Box width="100%">
          <PrevNext disabled={!value} text="다음" onClick={onNext} />
        </Box>
      </BaseStack>
      <Spacing gap={40} />
    </BaseStack>
  );
}
